import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, processingAPI } from '../../services/api';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Shield,
  User as UserIcon,
  Briefcase,
  List,
  LayoutGrid,
} from 'lucide-react';

const inputClass = 'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm';
const labelClass = 'block text-sm font-medium text-gray-700';

const emptyForm = {
  name: '', email: '', staff_no: '', password: '',
  shift_id: '',
  department_id: '', subdept_id: '', section_id: '', subsection_id: '',
  is_active: true, is_hr: false, is_flexi: false, is_excluded: false, is_friday_shift: false, is_intern_manager: false,
  start_date: '', end_date: '',
};

// Cascading dept selector — shared by modal and bulk shift modal
const DeptSelector = ({ form, onChange, departments, required = false }) => {
  const [subdepts, setSubdepts] = useState([]);
  const [sections, setSections] = useState([]);
  const [subsections, setSubsections] = useState([]);

  // When editing, pre-load the chain
  useEffect(() => {
    if (form.department_id) loadSubdepts(form.department_id, false);
    else { setSubdepts([]); setSections([]); setSubsections([]); }
  }, []); // only on mount — changes handled in handlers

  useEffect(() => {
    if (form.subdept_id) loadSections(form.subdept_id, false);
  }, []);

  useEffect(() => {
    if (form.section_id) loadSubsections(form.section_id, false);
  }, []);

  const loadSubdepts = async (deptId, clear = true) => {
    if (clear) { onChange({ subdept_id: '', section_id: '', subsection_id: '' }); setSections([]); setSubsections([]); }
    if (!deptId) { setSubdepts([]); return; }
    try {
      const res = await usersAPI.getDeptSubdepts(deptId);
      setSubdepts(res.data.subdepts);
    } catch { setSubdepts([]); }
  };

  const loadSections = async (subdeptId, clear = true) => {
    if (clear) { onChange({ section_id: '', subsection_id: '' }); setSubsections([]); }
    if (!subdeptId) { setSections([]); return; }
    try {
      const res = await usersAPI.getSubdeptSections(subdeptId);
      setSections(res.data.sections);
    } catch { setSections([]); }
  };

  const loadSubsections = async (sectionId, clear = true) => {
    if (clear) onChange({ subsection_id: '' });
    if (!sectionId) { setSubsections([]); return; }
    try {
      const res = await usersAPI.getSectionSubsections(sectionId);
      setSubsections(res.data.subsections);
    } catch { setSubsections([]); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-700 border-b pb-1">Department Assignment</p>
      <div>
        <label className={labelClass}>Department {required && <span className="text-red-500">*</span>}</label>
        <select
          className={inputClass}
          value={form.department_id}
          onChange={e => { onChange({ department_id: e.target.value }); loadSubdepts(e.target.value); }}
          required={required}
        >
          <option value="">— Select Department —</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      {subdepts.length > 0 && (
        <div>
          <label className={labelClass}>Sub-Department</label>
          <select
            className={inputClass}
            value={form.subdept_id}
            onChange={e => { onChange({ subdept_id: e.target.value }); loadSections(e.target.value); }}
          >
            <option value="">— None —</option>
            {subdepts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
      {sections.length > 0 && (
        <div>
          <label className={labelClass}>Section</label>
          <select
            className={inputClass}
            value={form.section_id}
            onChange={e => { onChange({ section_id: e.target.value }); loadSubsections(e.target.value); }}
          >
            <option value="">— None —</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
      {subsections.length > 0 && (
        <div>
          <label className={labelClass}>Subsection</label>
          <select
            className={inputClass}
            value={form.subsection_id}
            onChange={e => onChange({ subsection_id: e.target.value })}
          >
            <option value="">— None —</option>
            {subsections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [shiftFilter, setShiftFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [syncing, setSyncing] = useState(false);
  const [syncLog, setSyncLog] = useState([]);

  // Bulk actions
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkShiftModalOpen, setBulkShiftModalOpen] = useState(false);
  const [selectedBulkShift, setSelectedBulkShift] = useState('');

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await usersAPI.getUsers({
        page: viewMode === 'flags' ? 1 : currentPage,
        per_page: viewMode === 'flags' ? 1000 : 20,
        search: searchTerm, show_inactive: showInactive,
        ...(shiftFilter ? { shift_id: shiftFilter } : {}),
      });
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setSelectedUsers(new Set());
    } catch (err) {
      setError('Failed to load users: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, showInactive, shiftFilter, viewMode]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'boolean') return sortDir === 'asc' ? (bv - av) : (av - bv);
    return sortDir === 'asc'
      ? String(av ?? '').localeCompare(String(bv ?? ''))
      : String(bv ?? '').localeCompare(String(av ?? ''));
  });

  const SortIcon = ({ k }) => sortKey !== k ? null : (
    <span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
  );

  const handleSyncUsers = async () => {
    setSyncing(true);
    setSyncLog([]);
    setError('');
    setSuccessMessage('');
    try {
      const res = await processingAPI.syncUsers();
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const statusRes = await processingAPI.getJobStatus(jobId);
          const job = statusRes.data;
          if (job.status === 'done') {
            clearInterval(poll);
            setSyncing(false);
            const result = job.result || {};
            const entries = (result.log_entries || []).filter(e => e.level === 'ERROR' || e.level === 'SKIPPED');
            setSyncLog(entries);
            setSuccessMessage(
              `Sync complete — ${result.synced ?? 0} synced, ${result.skipped ?? 0} skipped, ${result.errors ?? 0} errors`
            );
            fetchUsers();
          } else if (job.status === 'error') {
            clearInterval(poll);
            setSyncing(false);
            setError('Sync failed: ' + (job.message || 'Unknown error'));
          }
        } catch {
          clearInterval(poll);
          setSyncing(false);
          setError('Failed to check sync status');
        }
      }, 3000);
    } catch (err) {
      setSyncing(false);
      setError(err.response?.data?.error || 'Failed to start sync');
    }
  };

  useEffect(() => {
    usersAPI.getShifts().then(r => setShifts(r.data.shifts)).catch(() => {});
    usersAPI.getDepartments().then(r => setDepartments(r.data.departments)).catch(() => {});
  }, []);

  // Selection
  const toggleUserSelection = (userId) => {
    const next = new Set(selectedUsers);
    next.has(userId) ? next.delete(userId) : next.add(userId);
    setSelectedUsers(next);
  };

  const toggleAllSelection = () => {
    setSelectedUsers(selectedUsers.size === users.length ? new Set() : new Set(users.map(u => u.id)));
  };

  // Bulk shift
  const handleBulkShiftUpdate = async () => {
    if (!selectedBulkShift || selectedUsers.size === 0) return;
    setLoading(true);
    try {
      const updates = Array.from(selectedUsers).map(userId => ({
        user_id: userId, shift_id: parseInt(selectedBulkShift),
      }));
      await usersAPI.batchUpdateUsers({ updates });
      setSuccessMessage(`Shift updated for ${selectedUsers.size} user(s).`);
      setBulkShiftModalOpen(false);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update shifts.');
      setLoading(false);
    }
  };

  // Modal helpers
  const patchForm = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData(emptyForm);
    setError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (u) => {
    setModalMode('edit');
    setSelectedUser(u);
    setFormData({
      name: u.name, email: u.email, staff_no: u.staff_no, password: '',
      shift_id: u.shift_id || '',
      department_id: u.department_id || '',
      subdept_id: u.subdept_id || '',
      section_id: u.section_id || '',
      subsection_id: u.subsection_id || '',
      is_active: u.is_active, is_hr: u.is_hr, is_flexi: u.is_flexi, is_excluded: u.is_excluded ?? false, is_friday_shift: u.is_friday_shift ?? false, is_intern_manager: u.is_intern_manager ?? false,
      start_date: u.start_date || '', end_date: u.end_date || '',
    });
    setError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    patchForm({ [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      const payload = {
        ...formData,
        department_id: formData.department_id || null,
        subdept_id: formData.subdept_id || null,
        section_id: formData.section_id || null,
        subsection_id: formData.subsection_id || null,
        shift_id: formData.shift_id || null,
      };
      if (modalMode === 'create') {
        await usersAPI.createUser(payload);
        setSuccessMessage('User created successfully');
      } else {
        if (!payload.password) delete payload.password;
        await usersAPI.updateUser(selectedUser.id, payload);
        setSuccessMessage('User updated successfully');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await usersAPI.deleteUser(userId);
      setSuccessMessage('User deactivated successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const getRoleBadge = (u) => {
    if (u.is_admin) return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"><Shield className="h-3 w-3 mr-1" />Admin</span>;
    if (u.is_hr) return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"><Briefcase className="h-3 w-3 mr-1" />HR</span>;
    return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><UserIcon className="h-3 w-3 mr-1" />Employee</span>;
  };

  if (!user?.is_admin && !user?.is_hr) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="text-gray-500">HR or Admin privileges required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                <p className="text-sm text-gray-500">Manage employees and their roles</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncUsers}
                disabled={syncing}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync from SQL'}
              </button>
              <button onClick={handleOpenCreateModal} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" /> Add User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
          <span className="text-green-700 text-sm">{successMessage}</span>
        </div>
      )}

      {/* Sync log — only shown when there are discrepancies */}
      {syncLog.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-yellow-300 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-sm font-semibold text-yellow-800">
                Sync discrepancies ({syncLog.length} {syncLog.length === 1 ? 'entry' : 'entries'})
              </span>
            </div>
            <button onClick={() => setSyncLog([])} className="text-yellow-500 hover:text-yellow-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="min-w-full text-xs divide-y divide-yellow-200">
              <thead className="bg-yellow-100">
                <tr>
                  {['Level', 'Staff No', 'Name', 'Reason'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-yellow-800 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-100">
                {syncLog.map((entry, i) => (
                  <tr key={i} className="hover:bg-yellow-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        entry.level === 'ERROR'   ? 'bg-red-100 text-red-700' :
                        entry.level === 'SKIPPED' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-yellow-200 text-yellow-800'
                      }`}>{entry.level}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{entry.code}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{entry.name}</td>
                    <td className="px-3 py-2 text-gray-600">{entry.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, or staff number..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <select
              value={shiftFilter}
              onChange={e => { setShiftFilter(e.target.value); setCurrentPage(1); }}
              className="border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Shifts</option>
              {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </select>
            <button onClick={() => { setSearchTerm(''); setShiftFilter(''); setCurrentPage(1); setShowInactive(false); }} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <RefreshCw className="h-4 w-4 mr-2" /> Reset
            </button>
            <div className="flex rounded-md border border-gray-300 overflow-hidden ml-2">
              <button onClick={() => setViewMode('list')} title="List view" className={`px-2 py-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}><List className="h-4 w-4" /></button>
              <button onClick={() => setViewMode('flags')} title="Flags overview" className={`px-2 py-2 border-l border-gray-300 ${viewMode === 'flags' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}><LayoutGrid className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center pl-4 border-l border-gray-200">
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input type="checkbox" className="sr-only" checked={showInactive} onChange={e => { setShowInactive(e.target.checked); setCurrentPage(1); }} />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${showInactive ? 'bg-blue-600' : 'bg-gray-300'}`} />
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showInactive ? 'translate-x-4' : ''}`} />
                </div>
                <div className="ml-3 text-sm text-gray-700 font-medium whitespace-nowrap">Show Inactive</div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedUsers.size > 0 && (
        <div className="bg-white p-4 shadow rounded-lg flex justify-between items-center">
          <span className="text-sm text-gray-700"><span className="font-semibold">{selectedUsers.size}</span> user(s) selected</span>
          <button onClick={() => setBulkShiftModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            Change Shift
          </button>
        </div>
      )}

      {/* Users table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : viewMode === 'flags' ? (
          <>
            {(() => {
              const FLAGS = [
                { key: 'is_active',       label: 'Active',      on: 'text-green-600' },
                { key: 'is_hr',           label: 'HR',          on: 'text-blue-600'  },
                { key: 'is_manager',      label: 'Manager',     on: 'text-purple-600'},
                { key: 'is_flexi',        label: 'Flexi',       on: 'text-yellow-600'},
                { key: 'is_friday_shift', label: 'Fri Shift',   on: 'text-orange-600'},
                { key: 'is_confirmed',    label: 'Confirmed',   on: 'text-green-600' },
                { key: 'is_excluded',       label: 'Excluded',      on: 'text-red-600'    },
                { key: 'is_intern_manager', label: 'Intern Mgr',    on: 'text-teal-600'   },
                // { key: 'wage_check',      label: 'Wage >4k',    on: 'text-indigo-600'},
              ];
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {[{key:'name',label:'Name',cls:'text-left sticky left-0 bg-gray-50'},{key:'staff_no',label:'Staff No',cls:'text-left'}].map(col => (
                          <th key={col.key} onClick={() => handleSort(col.key)} className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap ${col.cls}`}>
                            {col.label}<SortIcon k={col.key} />
                          </th>
                        ))}
                        {FLAGS.map(f => (
                          <th key={f.key} onClick={() => handleSort(f.key)} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none">{f.label}<SortIcon k={f.key} /></th>
                        ))}
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedUsers.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900 sticky left-0 bg-white">{u.name}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-500 font-mono text-xs">{u.staff_no}</td>
                          {FLAGS.map(f => (
                            <td key={f.key} className="px-3 py-2 text-center">
                              {u[f.key]
                                ? <span className={`font-bold ${f.on}`}>✓</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => handleOpenEditModal(u)} className="text-blue-600 hover:text-blue-900"><Edit2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <p className="text-sm text-gray-700">Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span></p>
                <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronRight className="h-5 w-5" /></button>
                </nav>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input type="checkbox" checked={users.length > 0 && selectedUsers.size === users.length} onChange={toggleAllSelection} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </th>
                    {['Name', 'Staff No', 'Role', 'Department', 'Sub-dept', 'Shift', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleUserSelection(u.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{u.name}</div>
                        <div className="text-gray-500 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">{u.staff_no}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{getRoleBadge(u)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{u.department_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{u.subdept_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {u.shift_name ? <>{u.shift_name}{u.shift_code && <span className="ml-1 text-xs text-gray-400">({u.shift_code})</span>}</> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => handleOpenEditModal(u)} className="text-blue-600 hover:text-blue-900 mr-3"><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:text-red-900" disabled={u.id === user.id}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <p className="text-sm text-gray-700">Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span></p>
                <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronRight className="h-5 w-5" /></button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit/Create modal */}
      {isModalOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50" role="dialog" aria-modal="true">
          <div className="flex items-start justify-center min-h-screen pt-10 px-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleCloseModal} />
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div className="px-6 pt-5 pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{modalMode === 'create' ? 'Add New User' : 'Edit User'}</h3>
                    <button type="button" onClick={handleCloseModal} className="text-gray-400 hover:text-gray-500"><X className="h-6 w-6" /></button>
                  </div>

                  {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>}

                  <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                    {/* Basic info */}
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input name="name" type="text" required value={formData.name} onChange={handleInputChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Email</label>
                      <input name="email" type="email" value={formData.email} onChange={handleInputChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Staff Number</label>
                      <input name="staff_no" type="text" required value={formData.staff_no} onChange={handleInputChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Password {modalMode === 'edit' && <span className="text-gray-400 font-normal">(leave blank to keep unchanged)</span>}</label>
                      <input name="password" type="password" required={modalMode === 'create'} value={formData.password} onChange={handleInputChange} className={inputClass} />
                    </div>

                    {/* Shift */}
                    <div>
                      <label className={labelClass}>Shift</label>
                      <select name="shift_id" value={formData.shift_id} onChange={handleInputChange} className={inputClass}>
                        <option value="">No Shift</option>
                        {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                      </select>
                    </div>

                    {/* Employment dates */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Start Date <span className="text-gray-400 font-normal">(attendance from)</span></label>
                        <input name="start_date" type="date" value={formData.start_date} onChange={handleInputChange} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>End Date <span className="text-gray-400 font-normal">(last day, blank if active)</span></label>
                        <input name="end_date" type="date" value={formData.end_date} onChange={handleInputChange} className={inputClass} />
                      </div>
                    </div>

                    {/* Department cascading */}
                    <DeptSelector
                      form={formData}
                      onChange={patchForm}
                      departments={departments}
                      required={modalMode === 'create'}
                    />

                    {/* Permissions */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 border-b pb-1">Permissions</p>
                      {[
                        { name: 'is_hr', label: 'HR Staff' },
                        { name: 'is_flexi', label: 'Flexi Hours' },
                        { name: 'is_friday_shift', label: 'Friday Shift Override' },
                        { name: 'is_excluded', label: 'Excluded from Processing' },
                        { name: 'is_intern_manager', label: 'Intern Manager' },
                        { name: 'is_active', label: 'Active' },
                      ].map(({ name, label }) => (
                        <label key={name} className="flex items-center">
                          <input type="checkbox" name={name} checked={formData[name]} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                          <span className="ml-2 text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3">
                  <button type="button" onClick={handleCloseModal} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                    {modalMode === 'create' ? 'Create User' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk shift modal */}
      {bulkShiftModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Bulk Change Shift</h3>
              <button onClick={() => setBulkShiftModalOpen(false)} className="text-gray-400 hover:text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={labelClass}>New shift for {selectedUsers.size} user(s)</label>
                <select value={selectedBulkShift} onChange={e => setSelectedBulkShift(e.target.value)} className={inputClass}>
                  <option value="">Select Shift</option>
                  {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setBulkShiftModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                <button onClick={handleBulkShiftUpdate} disabled={loading || !selectedBulkShift} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
